import React from "react";
import { v4 as uuidv4 } from "uuid";
import { Field } from "../../../shared/Field";
import {
	getWorkflowDefById,
} from "../../../../selectors/workflowSelectors";
import { useAppSelector } from "../../../../store";
import { ConfigurationPanelField, FieldSetField } from "../../../../slices/workflowSlice";

/**
 * This component renders the configuration panel for the selected workflow in the processing step of the new event
 * wizard chosen via dropdown.
 */
// interface RequiredFormProps {
// 	configuration?: { [key: string]: unknown }
// }
export type Configuration = { [key: string]: unknown }

const RenderWorkflowConfig = ({
	workflowId,
	configuration,
	configurationName,
	displayDescription,
}: {
	workflowId: string
	configuration: Configuration
	configurationName: string
	displayDescription?: boolean
}) => {

	const workflowDef = useAppSelector(state => getWorkflowDefById(state, workflowId));

	// Get html for configuration panel
	const configPanel = !!workflowDef && workflowDef.configurationPanelJson
		? workflowDef.configurationPanelJson
		: [];
	const description = !!workflowDef && workflowDef.description && !displayDescription
		? workflowDef.description
		: "";

	return (
		<WorkflowConfig
			configuration={configuration}
			configurationName={configurationName}
			configPanel={configPanel}
			description={description}
		/>
	);
};

export const WorkflowConfig = ({
	configuration,
	configurationName,
	configPanel,
	description,
}: {
	configuration: Configuration
	configurationName: string
	configPanel: string | ConfigurationPanelField[]
	description: string
}) => {

	return (
		<>
			{description.length > 0 && (
				<div id="workflow-configuration-description-box">
					<div id="workflow-configuration-description-text">{description.trim()}</div>
				</div>
			)}

			{Array.isArray(configPanel) && configPanel.length > 0 && (
				<form>
					<div id="workflow-configuration">
						{configPanel.map((configOption, key) => (
							<fieldset key={key}>
								{!!configOption.legend && (
									<legend>{configOption.legend}</legend>
								)}
								{!!configOption.description && (
									<p>{configOption.description}</p>
								)}
								<ul>
									{configOption.fieldset?.map((field, keys) =>
										renderInputByType(field, keys, configuration, configurationName),
									)}
								</ul>
							</fieldset>
						))}
					</div>
				</form>
			)}
		</>
	);
};

// render input depending on field type
const renderInputByType = (
	field: FieldSetField,
	key: React.Key | null | undefined,
	configuration: Configuration,
	configurationName: string,
) => {
	switch (field.type) {
		case "checkbox":
			return <RenderCheckbox field={field} key={key} configuration={configuration} configurationName={configurationName} />;
		case "radio":
			return <RenderRadio field={field} key={key} configuration={configuration} configurationName={configurationName} />;
		case "number":
			return <RenderNumber field={field} key={key} configuration={configuration} configurationName={configurationName} />;
		case "text":
			return <RenderText field={field} key={key} configuration={configuration} configurationName={configurationName} />;
		case "datetime-local":
			return <RenderDatetimeLocal field={field} key={key} configuration={configuration} configurationName={configurationName} />;
		default:
			return "";
	}
};

const RenderDatetimeLocal = (
	{ field, configuration, configurationName } : { field: FieldSetField, configuration: Configuration, configurationName: string }) => {
		return <RenderField field={field} configuration={configuration} configurationName={configurationName} />;
};

const RenderCheckbox = (
	{ field, configuration, configurationName } : { field: FieldSetField, configuration: Configuration, configurationName: string }) => {
		return <RenderField field={field} configuration={configuration} configurationName={configurationName} />;
};

const RenderRadio = (
	{ field, configuration, configurationName } : { field: FieldSetField, configuration: Configuration, configurationName: string }) => {

		return (
			<li>
				<div role="group" className="configField">
					{field.options?.map(option =>
						<label key={option.value}>
							<RenderField
								field={field}
								configuration={configuration}
								configurationName={configurationName}
							/>
							{option.label}
						</label>,
					)}
				</div>
			</li>
		);
};

const RenderNumber = (
	{ field, configuration, configurationName } : { field: FieldSetField, configuration: Configuration, configurationName: string }) => {
	// validate that value of number is between max and min
	const validate = (value: string) => {
		let error;
		if (field.max && field.min && (parseInt(value) > field.max || parseInt(value) < field.min)) {
			error = "out of range";
		}
		return error;
	};

		return <RenderField field={field} configuration={configuration} configurationName={configurationName} validate={validate}/>;
};

const RenderText = ({
	field,
	configuration,
	configurationName,
}: {
	field: FieldSetField,
	configuration: Configuration,
	configurationName: string,
}) => {
		return <RenderField field={field} configuration={configuration} configurationName={configurationName} />;
};

const RenderField = ({
	field,
	configuration,
	configurationName,
	validate = undefined,
}: {
	field: FieldSetField,
	configuration: Configuration,
	configurationName: string,
	validate?: (value: string) => string | undefined,
}) => {
	// id used for Field and label
	const uuid = uuidv4();
	const disabled = field.disabled ? field.disabled : false;

	const renderField = () => {
			return (
				<Field
					id={uuid}
					defaultValue={field.defaultValue}
					validate={validate}
					className="configField"
					name={configurationName + "." + field.name}
					disabled={disabled}
					type={field.type}
					min={field.min}
					max={field.max}
				/>
			);
	};

	return (
		<li>
			{renderField()}
			<label htmlFor={uuid}>{field.label as string}</label>
			{/* if input has an additional fieldset or further configuration inputs
						then render again by input type*/}
			{!!field.fieldset && !!configuration && !!configuration[field.name] && (
				<ul className="workflow-configuration-subpanel">
					{field.fieldset?.map((f, keys) => renderInputByType(f, keys, configuration, configurationName))}
				</ul>
			)}
		</li>
	);
};

export default RenderWorkflowConfig;
